import { DateTime } from 'luxon'
import capitalize from 'lodash/capitalize'
import { safeFormat } from 'lib/family-cup-utils/event-dates'
import {
  getProductName,
  getTransactionType,
  groupLineItems,
  sumField,
} from 'components/transaction-summary/transaction-utils'

const monthDayYearFormat = 'MM/dd/yyyy'

export const formatCurrency = amountInCents =>
  `${amountInCents < 0 ? '-$' : '$'}${(Math.abs(amountInCents) / 100).toFixed(
    2,
  )}`

export const byCreatedAt = (a, b) =>
  DateTime.fromISO(a.createdAt) > DateTime.fromISO(b.createdAt) ? 1 : -1

const byCreatedAtThenLineType = (a, b) => {
  if (a.createdAt === b.createdAt) {
    return a.lineType > b.lineType ? 1 : -1
  }
  return byCreatedAt(a, b)
}

export const lineItemLabel = ({
  createdAt,
  discountCode,
  label,
  lineType,
  refund,
}) => {
  if (refund) {
    return `Refund (${safeFormat(createdAt, monthDayYearFormat)})`
  }
  if (lineType === 'DISCOUNT') {
    return `${label} (${discountCode})`
  }
  return label
}

const getOrderTotals = (feeGroups, orderTotal) => {
  const coachingFees = feeGroups.find(item => item.group === 'COACHING_FEES')
  const offlineFees =
    coachingFees?.lineItems.find(
      item => item.label === 'Coaching Fee (Offline)',
    )?.amountInCents * -1 || 0

  return {
    orderTotal,
    offlineFees,
    consumerTotal: orderTotal + offlineFees,
  }
}

const getPaymentMethod = (
  { cardBrand, last4 },
  paymentSourceType,
  initialTransaction,
) => {
  if (cardBrand && last4) {
    return `${cardBrand.toUpperCase()} ending in ${last4}`
  }

  const source = paymentSourceType.enumValues.find(
    sourceType => sourceType.name === initialTransaction.sourceType,
  )?.description

  return source ? `${source} (collected offline)` : 'Collected offline'
}

export const getTransactionReceipt = (
  registrationPayment,
  paymentSourceType,
) => {
  const {
    payor,
    transactions,
    products,
    registrationPaymentCharge,
    ...payment
  } = registrationPayment

  const feeGroups = groupLineItems(transactions).map(group => ({
    ...group,
    lineItems: [...group.lineItems]
      .sort(byCreatedAtThenLineType)
      .map(lineItem => ({
        ...lineItem,
        displayLabel: lineItemLabel(lineItem),
      })),
  }))
  const orderTotal = feeGroups.reduce(sumField('subtotal'), 0)
  const initialTransaction = [...transactions].sort(byCreatedAt)[0]

  const chargeDetails = registrationPaymentCharge
    ? [
      `${capitalize(registrationPaymentCharge.chargeType)} Number: ${
        registrationPaymentCharge.chargeNumber
      }`,
      ...(registrationPaymentCharge.checkNumber
        ? [`Check Number: ${registrationPaymentCharge.checkNumber}`]
        : []),
    ]
    : []

  return {
    id: payment.id,
    payorName: payor.name || `${payor.firstName} ${payor.lastName}`,
    payorEmail: payor.email,
    purchaseDate: safeFormat(payment.createdAt, monthDayYearFormat),
    paymentMethod: getPaymentMethod(
      payment,
      paymentSourceType,
      initialTransaction,
    ),
    chargeDetails,
    product: getProductName(products[0]),
    transactionType: getTransactionType(products[0]),
    lastUpdated: safeFormat(payment.updatedAt, monthDayYearFormat),
    initialTransaction,
    feeGroups,
    totals: getOrderTotals(feeGroups, orderTotal),
  }
}
